// Components/InfiniteScrollDropdown.tsx

import { useState, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Spin, Input, Radio, Divider } from 'antd';
import styled from 'styled-components';
import { greyColor1, maxTablet, primaryColor, primaryFont, secondaryFont, textBlack, wMedium, primaryHeadingColor, maxWidth, retina } from '../../../Utils/utilsStyle';
import { validateSearchQuery } from '../../../Utils/UtilityFunctions';
import SearchGlobalIcon from '../assets/svgIcons/SearchGlobalIcon';
import Cross from '../../globalSearch/assets/Cross';

const SearchWrapper = styled.div`
  padding: 8px 16px 0px 16px;
  .search-icon-wrapper {
    gap: 4px;
    .search-global-icon-svg path {
      fill: ${primaryHeadingColor};
    }
    .ant-divider {
      border: 1px solid #DDD;
      margin: 0px;
      top: 0;
    }
  }
  .cross-icon {
    z-index: 1;
    position: absolute;
    right: 34px;
    top: 16px;
  }
  .ant-input-affix-wrapper {
    position: relative;
    border-radius: 18px;
    .ant-input-prefix {
      padding: 0px;
      margin-inline-end: 8px;
      @media all and (max-width: ${maxWidth}), ${retina} {
        padding: 0px;
      }
    }
    input::placeholder {
      color: ${greyColor1} !important;
      font-size: 14px !important;
      font-style: italic !important;
      @media all and (max-width: ${maxTablet}) {
        font-size: 12px !important;
      }
    }
  }
`;

const OptionItemWrapper = styled.div`
  padding: 0 16px;
`;

const OptionItem = styled.div<{ $isActive: boolean }>`
  font-family: ${primaryFont};
  font-size: 16px;
  font-weight: 500;
  color: #67696A;
  letter-spacing: -0.609px;
  padding-bottom: 8px;
  cursor: pointer;
  @media all and (max-width: ${maxTablet}) {
    font-size: 14px;
  }
`;

const OptionLabel = styled.span`
  flex: 1;
`;

const LoaderWrapper = styled.div<{ isSearchLoading?: boolean }>`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.isSearchLoading ? '30px 0px 16px 0px' : '16px 0'};
`;

const OptionsContainer = styled.div<{ maxHeight?: string }>`
  max-height: ${props => props.maxHeight || '287px'};
  overflow-y: auto;
  margin-right: 4px;
  &::-webkit-scrollbar {
    width: 5px;
    border-radius:8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #001760;
    border-radius: 12px;
  }
  &::-webkit-scrollbar-track {
    background: #CFDAE8;
    border-radius: 12px;
  }
  .infinite-scroll-component {
    min-height: 100px;
    position: relative;
    padding-top: 8px;
  }
`;

const OptionCard = styled.div`
  gap: 8px;
  .card-content {
    .card-title {
      color: ${textBlack};
      font-family: ${secondaryFont};
      font-size: 14px;
      font-weight: ${wMedium};
      letter-spacing: -0.277px;
      @media all and (max-width: ${maxTablet}) {
        font-size: 12px;
      }
    }
    .card-subtitle {
      color: ${primaryColor};
      font-family: ${primaryFont};
      font-size: 10px;
      font-weight: ${wMedium};
    }
  }
  .img-wrapper {
    width: 39px;
    height: 39px; 
    border-radius: 50%;
    border: 1px solid #F0B520;
    img {
      width: 39px;
      height: 39px;
      object-fit: cover;
      border-radius: 50%;
    }
  }   
`;

const NoDataFound = styled.div`
  width: 100%;
  height: 80%;
  display: flex;
  align-items: center;
  position: absolute;
  justify-content: center;
  overflow: hidden;
`

export interface OptionType {
  id: number;
  label: string;
  image?: string;
  subtitle?: string;
  [key: string]: any;
}

interface InfiniteScrollDropdownProps {
  options: OptionType[];
  selectedId: number;
  onSelect: (id: number) => void;
  hasSearch?: boolean;
  searchPlaceholder?: string;
  hasMore?: boolean;
  loadMore?: () => void;
  maxHeight?: string;
  isLoading?: boolean;
  isCardOption?: boolean;
  onSearchSubmit?: (term: string) => void;
  useRemoteSearch?: boolean;
  initialSearchTerm?: string;
}

export const InfiniteScrollDropdown: React.FC<InfiniteScrollDropdownProps> = ({
  options,
  selectedId,
  onSelect,
  hasSearch = false,
  searchPlaceholder = "Search...",
  hasMore = false,
  loadMore = () => { },
  maxHeight = "300px",
  isLoading = false,
  isCardOption = false,
  onSearchSubmit,
  useRemoteSearch = false,
  initialSearchTerm = '',
}) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const filteredOptions = hasSearch && searchTerm && !useRemoteSearch
    ? options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  const handleSelect = (id: number) => {

    onSelect(id);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const trimmd = e?.target?.value.trim();
    const isWhitespaceOnly = e?.target?.value.length > 0 && trimmd.length === 0;
    if (isWhitespaceOnly) return;

    const searchQuery = validateSearchQuery(e?.target?.value);
    if (searchQuery.length === 0 && searchTerm.length > 0) {
      clearSearch();
      return;
    }

    setSearchTerm(searchQuery);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearchSubmit?.(searchTerm.trim());
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    if (useRemoteSearch) {
      onSearchSubmit?.('');
    }
  }

  return (
    <>
      {hasSearch && (
        <SearchWrapper className='search-wrapper'>
          <Input
            tabIndex={-1}
            className='infinite-dropdown-search'
            size="large"
            placeholder={searchPlaceholder}
            disabled={false}
            prefix={<div className='search-icon-wrapper d-flex align-items-center'><SearchGlobalIcon /><Divider type="vertical" /></div>}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
          />
          <Cross className={`cross-icon search-field-icon ${searchTerm.length > 0 ? '' : 'disabled'}`} onClick={clearSearch} />
        </SearchWrapper>
      )}

      <OptionsContainer
        id="scrollable-dropdown"
        maxHeight={maxHeight}
        ref={scrollContainerRef}
      >
        <InfiniteScroll
          dataLength={filteredOptions.length}
          next={loadMore}
          hasMore={hasMore && (!searchTerm || useRemoteSearch)}
          loader={
            <LoaderWrapper isSearchLoading={false}>
              <Spin size="small" />
            </LoaderWrapper>
          }
          scrollableTarget="scrollable-dropdown"
        >
          {filteredOptions.length > 0
            ? filteredOptions.map((option) => (
              <OptionItemWrapper key={option.id} className="d-flex flex-column">
                <OptionItem
                  className="d-flex align-items-center"
                  $isActive={selectedId === option.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(option.id);
                  }}
                >
                  <Radio checked={selectedId === option.id} onChange={() => handleSelect(option.id)} />
                  {isCardOption ? (
                    <OptionCard className="d-flex align-items-center">
                      <div className="img-wrapper d-flex align-items-center">
                        <img src={option.image} alt={option.label} />
                      </div>
                      <div className="card-content d-flex flex-column">
                        <span className="card-title">{option.label}</span>
                        {option.subtitle ? (
                          <span className="card-subtitle">{option.subtitle}</span>
                        ) : null}
                      </div>
                    </OptionCard>
                  ) : (
                    <OptionLabel>{option.label}</OptionLabel>
                  )}
                </OptionItem>
              </OptionItemWrapper>
            ))
            : filteredOptions.length === 0 && !isLoading
              ? <NoDataFound>No data found</NoDataFound>
              : isLoading && filteredOptions.length === 0
                ? <LoaderWrapper isSearchLoading={true}><Spin /></LoaderWrapper>
                : null
          }
        </InfiniteScroll>

      </OptionsContainer>
    </>
  );
};